import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { constants, findEntityById, currencyString } from "@qgiv/core-js";
import { useFormikContext } from "formik";
import { selectCmsControlByIdByPage } from "../../../redux/slices/cmsSettingsSlice";
import { selectHasParentRegistration } from "../../../redux/slices/appSettingsSlice";
import { selectSpecificFormSetting } from "../../../redux/slices/formSettingsSlice";
import { selectTeams } from "../../../redux/slices/teamsSlice";
import { selectAllRegistration } from "../../../redux/slices/registrationSlice";
import { selectAllCategories } from "../../../redux/slices/categoriesSlice";
import { selectAllP2pSettings } from "../../../redux/slices/p2pSettingsSlice";
import { selectIsCms } from "../../../redux/slices/configSlice";
import FundraisingGoal from "./FundraisingGoal";

const ConnectedFundraisingGoal = ({ controlId }) => {
    const {
        ENUMS: {
            DefaultView,
            CategorySetting,
            EntityType: { TEAM },
        },
    } = constants;
    const { MULTI_CATEGORY } = CategorySetting;
    const { values } = useFormikContext();
    const {
        Category = [],
        Registration_Role = [],
        Team = "",
        Team_Captain = false,
        Managed_By_Primary = false,
        New_Team_Fundraising_Goal = "",
    } = values;
    const isCms = useSelector(selectIsCms);
    const control = useSelector((state) =>
        selectCmsControlByIdByPage(
            controlId,
            DefaultView.REGISTRATION_DETAILS_STEP,
            state,
        ),
    );

    const hasParentRegistration = useSelector(selectHasParentRegistration);
    const currency = useSelector((state) =>
        selectSpecificFormSetting(state, "currency"),
    );
    const teams = useSelector(selectTeams);
    const registration = useSelector(selectAllRegistration);
    const categories = useSelector(selectAllCategories);

    const {
        allowPrimaryToManageSubFundraising,
        categorySystemType,
        enableClassifications,
        enableNonFundraising,
        primaryNonFundraising,
        additionalNonFundraising,
        minParticipantGoal,
        enableCommitments,
    } = useSelector(selectAllP2pSettings);

    const { currentRegistration, classification, registrations } = registration;

    const { Classification } = classification;
    const isAdditionalParticipant = currentRegistration > 0;
    const multiCategory =
        categorySystemType === MULTI_CATEGORY && categories.length > 1;

    const categoriesSettings = categories.find(
        findEntityById(Number(Category[0])),
    );

    const categoryId = Number(Category[0]);

    // TODO: replace with bool once BE change in place.
    const hasCommitment = !!(
        (categoriesSettings && categoriesSettings.enableCommitments) ||
        (enableCommitments && isCms)
    );

    // If Classifications are enabled, check which categories apply to the classification selected
    const hasClassifications =
        enableClassifications && Classification && Classification.length > 0;
    const filteredByClassification =
        hasClassifications &&
        categories.filter((filteredCategory) => {
            const { attachedClassifications = [] } = filteredCategory;
            // If attachedClassifications is an empty array, return true on filter
            return attachedClassifications.length === 0
                ? true
                : attachedClassifications.includes(Number(Classification[0]));
        });

    // Check if any of the applicable categories have the ability to be fundraisers
    // If all are NFP only we will not render the Change Category link on NfpCategoryOnly component
    const hasFundraisingCategories = hasClassifications
        ? filteredByClassification.some((element) => element.attachFundraising)
        : categories.some((element) => element.attachFundraising);

    // Determines whether special NFP Only content should display
    const isNfpOnly =
        enableNonFundraising &&
        categoriesSettings &&
        !categoriesSettings.attachFundraising &&
        categoriesSettings.attachNonfundraising;

    // Needed to decide if nfp checkbox is displayed
    const isFundraisingOnly =
        !enableNonFundraising ||
        (categoriesSettings &&
            categoriesSettings.attachFundraising &&
            !categoriesSettings.attachNonfundraising);

    // Primary Team Captains can not register as NFP
    const primaryRegistrantIsTeamCaptain = !!(
        currentRegistration === 0 &&
        Registration_Role[0] === `${TEAM}` &&
        Team_Captain
    );

    // Check if Primary is NFP, NFP registrations are not permitted to manage other
    // fundraising pages as they do not have one themselves
    const primaryRegistrantIsNfp = !!registrations[0].Is_Nfp;

    // value for when current registrant is either additional in current registration
    // or is being added to an existing registration through fundraising center
    const isChildParticipant = isAdditionalParticipant || hasParentRegistration;

    // Pays attention to event level allow primary to manage additional
    const manageIsEnabled =
        isCms ||
        (isChildParticipant &&
            allowPrimaryToManageSubFundraising === 1 &&
            !primaryRegistrantIsNfp);

    // Pays attention to event level settings
    const nfpIsEnabled =
        (enableNonFundraising &&
            !isAdditionalParticipant &&
            primaryNonFundraising) ||
        (isAdditionalParticipant &&
            additionalNonFundraising &&
            Managed_By_Primary === false);

    const {
        options: { fundraisingGoal },
    } = control;
    const isTeam = Registration_Role[0] === `${TEAM}`;
    const isNewTeam =
        isTeam && Team === "new-team" && New_Team_Fundraising_Goal;

    //  TODO: May need to add conditions in here for commitment cases
    const {
        description = "How much will you try to raise?",
        descriptionforAdditionalParticipants = "How much will they try to raise?",
        manageFundraisingCheckboxLabel = "I'll manage their fundraising",
        nonFundraisingParticipantCheckboxDisclaimer = "Are you sure? You won't get a customizable fundraising page to share with friends or to collect donations toward a personal goal.",
        nonFundraisingParticipantCheckboxLabel = "You won't get a fundraising goal and page with this type of registration.",
        nonFundraisingParticipantCheckboxLabelforAdditionalParticipants = "They don't want their own fundraising goal and page",
        nonFundraisingParticipantOnlyLabel = "You won't get a fundraising goal and page with this type of registration.",
        nonFundraisingParticipantOnlyPrompt = "Want to fundraise? Try changing your registration this is the prompt",
        sectionHeading = "Your Fundraising Goal",
        sectionHeadingforAdditionalParticipants = "Fundraising Goal",
        teamGoalDescription = "Money raised counts toward your goal + your team's goal of %TeamGoal%!",
        teamGoalDescriptionforAdditionalParticipants = "Money raised counts toward their goal + their team's goal of %TeamGoal%!",
        commitmentDetailsAdditionalParticipants = "They're only responsible for their %CommitmentAmount% commitment, even if they set a higher goal. If they raise less than %CommitmentAmount% by %CommitmentDeadline%, you agree to cover the difference. Once registered, they can add their own payment method to cover the difference in their fundraising account.",
        commitmentDetails = "You're only responsible for your %CommitmentAmount% commitment, even if you set a higher goal. If you raise less than %CommitmentAmount% by %CommitmentDeadline%, you agree to cover the difference.",
    } = fundraisingGoal;

    const commitmentDetailDisclaimers = {
        commitmentDetails,
        commitmentDetailsAdditionalParticipants,
    };

    const allowsZeroDollarValue = Number(minParticipantGoal) === 0;

    const renderTeamGoalDescription = () => {
        const selectedTeam =
            isTeam && Team.length > 0 && teams.length > 0
                ? teams.find((team) => team.id === Number(Team))
                : null;

        const newTeamGoal = isNewTeam ? Number(New_Team_Fundraising_Goal) : 0;

        const teamGoal = selectedTeam
            ? selectedTeam.fundraisingGoal
            : newTeamGoal;

        const displayTeamGoalDescription =
            (selectedTeam || isNewTeam) && teamGoal;
        const goalDescriptionText = isChildParticipant
            ? teamGoalDescriptionforAdditionalParticipants
            : teamGoalDescription;
        const splitGoalDescription = goalDescriptionText.split("%TeamGoal%");
        return (
            <p>
                {displayTeamGoalDescription ? (
                    <>
                        <span>{splitGoalDescription[0]}</span>
                        <span>
                            <strong>
                                {currencyString(teamGoal, currency)}
                            </strong>
                        </span>
                        <span>{splitGoalDescription[1]}</span>
                    </>
                ) : (
                    ""
                )}
            </p>
        );
    };

    const fundraisingGoalProps = {
        allowsZeroDollarValue,
        categoryId,
        description: isChildParticipant
            ? descriptionforAdditionalParticipants
            : description,
        nonFundraisingParticipantCheckboxDisclaimer,
        checkboxLabel: isChildParticipant
            ? nonFundraisingParticipantCheckboxLabelforAdditionalParticipants
            : nonFundraisingParticipantCheckboxLabel,
        hasFundraisingCategories,
        heading: isChildParticipant
            ? sectionHeadingforAdditionalParticipants
            : sectionHeading,
        nonFundraisingParticipantOnlyLabel,
        nonFundraisingParticipantOnlyPrompt,
        manageFundraisingCheckboxLabel,
        manageIsEnabled,
        multiCategory,
        isFundraisingOnly,
        isNfpOnly,
        isTeam,
        nfpIsEnabled,
        primaryRegistrantIsTeamCaptain,
        renderTeamGoalDescription,
        categoriesSettings,
        hasCommitment,
        commitmentDetailDisclaimers,
    };
    return <FundraisingGoal {...fundraisingGoalProps} />;
};

ConnectedFundraisingGoal.propTypes = {
    controlId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
};

export default ConnectedFundraisingGoal;
